// IEC-inspired electrical symbols. Stroke-based so they tint with currentColor.
// All element-icon viewBox 24x24, 1.6 stroke width — tuned for both palette (size 22) and canvas (size 28).
// Ported from prototype panelforge_redesign/icons.jsx.
//
// NOTE (i18n): label/sub in ELEMENT_CATALOG are kept as literal RU strings here,
// matching the prototype. They will be moved to i18n keys in the dedicated i18n step (Wave 3).
// Do not block this step on translation.

import type { FC } from 'react';

export interface IconProps {
  size?: number;
  color?: string;
}

const STROKE = 1.6;

// ─── Lighting ──────────────────────────────────────────────────────────────

export const LampIcon: FC<IconProps> = ({ size = 24, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="6.5" stroke={color} strokeWidth={STROKE} />
    <path d="M7.4 7.4l9.2 9.2M7.4 16.6l9.2-9.2" stroke={color} strokeWidth={STROKE} strokeLinecap="round" />
  </svg>
);

export const ExhaustFanIcon: FC<IconProps> = ({ size = 24, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <rect x="4" y="4" width="16" height="16" rx="2" stroke={color} strokeWidth={STROKE} opacity="0.75" />
    <circle cx="12" cy="12" r="5.4" stroke={color} strokeWidth={STROKE} />
    <circle cx="12" cy="12" r="1.2" fill={color} />
    <path d="M12 6.9c1.3 1.6 1.3 3.1 0 4.4-1.3-1.3-1.3-2.8 0-4.4z" fill={color} opacity="0.75" />
    <path d="M16.4 14.6c-2 .4-3.3-.4-3.9-2.2 1.8-.5 3.1.2 3.9 2.2z" fill={color} opacity="0.75" />
    <path d="M7.6 14.6c.8-2 2.1-2.7 3.9-2.2-.6 1.8-1.9 2.6-3.9 2.2z" fill={color} opacity="0.75" />
  </svg>
);

export const SpotIcon: FC<IconProps> = ({ size = 24, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="6.5" stroke={color} strokeWidth={STROKE} />
    <circle cx="12" cy="12" r="2.6" fill={color} />
  </svg>
);

export const SwitchIcon: FC<IconProps> = ({ size = 24, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <rect x="4" y="4" width="16" height="16" rx="2" stroke={color} strokeWidth={STROKE} opacity="0.75" />
    <circle cx="9.5" cy="15.5" r="1.35" fill={color} />
    <path d="M9.5 15.5l5-5.4" stroke={color} strokeWidth={STROKE} strokeLinecap="round" />
  </svg>
);

export const DoubleSwitchIcon: FC<IconProps> = ({ size = 24, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <rect x="4" y="4" width="16" height="16" rx="2" stroke={color} strokeWidth={STROKE} opacity="0.75" />
    <line x1="12" y1="5.5" x2="12" y2="18.5" stroke={color} strokeWidth={STROKE * 0.75} opacity="0.45" />
    <circle cx="8" cy="15.5" r="1.25" fill={color} />
    <circle cx="14.5" cy="15.5" r="1.25" fill={color} />
    <path d="M8 15.5l2.2-5" stroke={color} strokeWidth={STROKE} strokeLinecap="round" />
    <path d="M14.5 15.5l2.2-5" stroke={color} strokeWidth={STROKE} strokeLinecap="round" />
  </svg>
);

export const MasterSwitchIcon: FC<IconProps> = ({ size = 24, color = 'currentColor' }) => (
  // Wall plate with power symbol — distinctive enough at small sizes
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <rect x="3" y="3" width="18" height="18" rx="2.2" stroke={color} strokeWidth={STROKE} />
    <path d="M8.5 11a4 4 0 107 0" stroke={color} strokeWidth={STROKE} strokeLinecap="round" fill="none" />
    <line x1="12" y1="7.5" x2="12" y2="12" stroke={color} strokeWidth={STROKE} strokeLinecap="round" />
  </svg>
);

// ─── Sockets ───────────────────────────────────────────────────────────────

export const Socket1Icon: FC<IconProps> = ({ size = 24, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <rect x="4" y="4" width="16" height="16" rx="2" stroke={color} strokeWidth={STROKE} opacity="0.75" />
    <circle cx="12" cy="12" r="5.2" stroke={color} strokeWidth={STROKE} />
    <circle cx="9.5" cy="11" r="1.1" fill={color} />
    <circle cx="14.5" cy="11" r="1.1" fill={color} />
    <path d="M12 14v2" stroke={color} strokeWidth={STROKE} strokeLinecap="round" />
  </svg>
);

export const Socket2Icon: FC<IconProps> = ({ size = 24, color = 'currentColor' }) => (
  <svg x={-(size * 0.25)} width={size * 1.5} height={size} viewBox="0 0 36 24" fill="none">
    <rect x="4" y="4" width="28" height="16" rx="2" stroke={color} strokeWidth={STROKE} opacity="0.75" />
    <circle cx="12" cy="12" r="5.2" stroke={color} strokeWidth={STROKE} />
    <circle cx="9.5" cy="11" r="1.1" fill={color} />
    <circle cx="14.5" cy="11" r="1.1" fill={color} />
    <path d="M12 14v2" stroke={color} strokeWidth={STROKE} strokeLinecap="round" />
    <circle cx="24" cy="12" r="5.2" stroke={color} strokeWidth={STROKE} />
    <circle cx="21.5" cy="11" r="1.1" fill={color} />
    <circle cx="26.5" cy="11" r="1.1" fill={color} />
    <path d="M24 14v2" stroke={color} strokeWidth={STROKE} strokeLinecap="round" />
  </svg>
);

export const Socket3Icon: FC<IconProps> = ({ size = 24, color = 'currentColor' }) => (
  <svg x={-(size * 0.5)} width={size * 2} height={size} viewBox="0 0 48 24" fill="none">
    <rect x="4" y="4" width="40" height="16" rx="2" stroke={color} strokeWidth={STROKE} opacity="0.75" />
    <circle cx="12" cy="12" r="5.2" stroke={color} strokeWidth={STROKE} />
    <circle cx="9.5" cy="11" r="1.1" fill={color} />
    <circle cx="14.5" cy="11" r="1.1" fill={color} />
    <path d="M12 14v2" stroke={color} strokeWidth={STROKE} strokeLinecap="round" />
    <circle cx="24" cy="12" r="5.2" stroke={color} strokeWidth={STROKE} />
    <circle cx="21.5" cy="11" r="1.1" fill={color} />
    <circle cx="26.5" cy="11" r="1.1" fill={color} />
    <path d="M24 14v2" stroke={color} strokeWidth={STROKE} strokeLinecap="round" />
    <circle cx="36" cy="12" r="5.2" stroke={color} strokeWidth={STROKE} />
    <circle cx="33.5" cy="11" r="1.1" fill={color} />
    <circle cx="38.5" cy="11" r="1.1" fill={color} />
    <path d="M36 14v2" stroke={color} strokeWidth={STROKE} strokeLinecap="round" />
  </svg>
);

export const Socket4Icon: FC<IconProps> = ({ size = 24, color = 'currentColor' }) => (
  <svg x={-(size * 0.75)} width={size * 2.5} height={size} viewBox="0 0 60 24" fill="none">
    <rect x="4" y="4" width="52" height="16" rx="2" stroke={color} strokeWidth={STROKE} opacity="0.75" />
    <circle cx="12" cy="12" r="5.2" stroke={color} strokeWidth={STROKE} />
    <circle cx="9.5" cy="11" r="1.1" fill={color} />
    <circle cx="14.5" cy="11" r="1.1" fill={color} />
    <path d="M12 14v2" stroke={color} strokeWidth={STROKE} strokeLinecap="round" />
    <circle cx="24" cy="12" r="5.2" stroke={color} strokeWidth={STROKE} />
    <circle cx="21.5" cy="11" r="1.1" fill={color} />
    <circle cx="26.5" cy="11" r="1.1" fill={color} />
    <path d="M24 14v2" stroke={color} strokeWidth={STROKE} strokeLinecap="round" />
    <circle cx="36" cy="12" r="5.2" stroke={color} strokeWidth={STROKE} />
    <circle cx="33.5" cy="11" r="1.1" fill={color} />
    <circle cx="38.5" cy="11" r="1.1" fill={color} />
    <path d="M36 14v2" stroke={color} strokeWidth={STROKE} strokeLinecap="round" />
    <circle cx="48" cy="12" r="5.2" stroke={color} strokeWidth={STROKE} />
    <circle cx="45.5" cy="11" r="1.1" fill={color} />
    <circle cx="50.5" cy="11" r="1.1" fill={color} />
    <path d="M48 14v2" stroke={color} strokeWidth={STROKE} strokeLinecap="round" />
  </svg>
);

export const Socket3phIcon: FC<IconProps> = ({ size = 24, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="7.2" stroke={color} strokeWidth={STROKE} />
    <circle cx="12" cy="7.5" r="1.1" fill={color} />
    <circle cx="8" cy="14.2" r="1.1" fill={color} />
    <circle cx="16" cy="14.2" r="1.1" fill={color} />
  </svg>
);

export const BathSocketIcon: FC<IconProps> = ({ size = 24, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <rect x="4" y="4" width="16" height="16" rx="2" stroke={color} strokeWidth={STROKE} opacity="0.75" />
    <rect x="6.8" y="6.8" width="10.4" height="10.4" rx="1.2" stroke={color} strokeWidth={STROKE} />
    <circle cx="9.5" cy="11" r="1.1" fill={color} />
    <circle cx="14.5" cy="11" r="1.1" fill={color} />
    <path d="M12 14v2" stroke={color} strokeWidth={STROKE} strokeLinecap="round" />
  </svg>
);

export const KitchenSocketIcon: FC<IconProps> = ({ size = 24, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <rect x="4" y="4" width="16" height="16" rx="2" stroke={color} strokeWidth={STROKE} opacity="0.75" />
    <circle cx="12" cy="12" r="5.2" fill={color} opacity="0.85" />
  </svg>
);

export const TvSocketIcon: FC<IconProps> = ({ size = 24, color = 'currentColor' }) => (
  // Coaxial / antenna jack — concentric rings with a centre pin
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <rect x="4" y="4" width="16" height="16" rx="2" stroke={color} strokeWidth={STROKE} opacity="0.75" />
    <circle cx="12" cy="12" r="5" stroke={color} strokeWidth={STROKE} />
    <circle cx="12" cy="12" r="2.2" stroke={color} strokeWidth={STROKE} />
    <circle cx="12" cy="12" r="0.9" fill={color} />
  </svg>
);

export const Rj45SocketIcon: FC<IconProps> = ({ size = 24, color = 'currentColor' }) => (
  // RJ45 / data jack — modular connector silhouette with contact pins
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <rect x="4" y="4" width="16" height="16" rx="2" stroke={color} strokeWidth={STROKE} opacity="0.75" />
    <path d="M8 9H16V12.4H14V14H10V12.4H8Z" stroke={color} strokeWidth={STROKE} strokeLinejoin="round" />
    <path
      d="M9.7 9.6v2.2M11.2 9.6v2.2M12.8 9.6v2.2M14.3 9.6v2.2"
      stroke={color}
      strokeWidth={STROKE * 0.7}
      strokeLinecap="round"
    />
  </svg>
);

// ─── Appliances — distinctive silhouettes ────────────────────────────────────

export const WasherIcon: FC<IconProps> = ({ size = 24, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <rect x="4" y="3.5" width="16" height="17" rx="1.5" stroke={color} strokeWidth={STROKE} />
    <circle cx="12" cy="13" r="5" stroke={color} strokeWidth={STROKE} />
    <circle cx="7.5" cy="6.5" r="0.7" fill={color} />
    <circle cx="16.5" cy="6.5" r="0.7" fill={color} />
  </svg>
);

export const OvenIcon: FC<IconProps> = ({ size = 24, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <rect x="3.5" y="3.5" width="17" height="17" rx="1.5" stroke={color} strokeWidth={STROKE} />
    <rect x="6" y="9" width="12" height="9" rx="0.8" stroke={color} strokeWidth={STROKE} />
    <circle cx="7.5" cy="6.5" r="0.6" fill={color} />
    <circle cx="10" cy="6.5" r="0.6" fill={color} />
    <circle cx="12.5" cy="6.5" r="0.6" fill={color} />
    <line x1="15" y1="6.5" x2="18" y2="6.5" stroke={color} strokeWidth={STROKE} />
  </svg>
);

export const HobIcon: FC<IconProps> = ({ size = 24, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <rect x="3.5" y="3.5" width="17" height="17" rx="1.5" stroke={color} strokeWidth={STROKE} />
    <circle cx="8.5" cy="8.5" r="2.5" stroke={color} strokeWidth={STROKE} />
    <circle cx="15.5" cy="8.5" r="2.5" stroke={color} strokeWidth={STROKE} />
    <circle cx="8.5" cy="15.5" r="2.5" stroke={color} strokeWidth={STROKE} />
    <circle cx="15.5" cy="15.5" r="2.5" stroke={color} strokeWidth={STROKE} />
  </svg>
);

export const MicrowaveIcon: FC<IconProps> = ({ size = 24, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <rect x="2.5" y="6" width="19" height="12" rx="1.5" stroke={color} strokeWidth={STROKE} />
    <rect x="5" y="8.5" width="10" height="7" rx="0.6" stroke={color} strokeWidth={STROKE} />
    <circle cx="18" cy="10" r="0.8" fill={color} />
    <circle cx="18" cy="13" r="0.8" fill={color} />
  </svg>
);

export const HoodIcon: FC<IconProps> = ({ size = 24, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path d="M5 6h14l-2 7H7L5 6z" stroke={color} strokeWidth={STROKE} strokeLinejoin="round" />
    <path d="M9 16v3M12 16v3M15 16v3" stroke={color} strokeWidth={STROKE} strokeLinecap="round" opacity="0.55" />
  </svg>
);

export const FridgeIcon: FC<IconProps> = ({ size = 24, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <rect x="5.5" y="2.5" width="13" height="19" rx="1.5" stroke={color} strokeWidth={STROKE} />
    <line x1="5.5" y1="10" x2="18.5" y2="10" stroke={color} strokeWidth={STROKE} />
    <line x1="8" y1="6" x2="8" y2="8" stroke={color} strokeWidth={STROKE} strokeLinecap="round" />
    <line x1="8" y1="13" x2="8" y2="16" stroke={color} strokeWidth={STROKE} strokeLinecap="round" />
  </svg>
);

export const FreezerIcon: FC<IconProps> = ({ size = 24, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <rect x="5.5" y="2.5" width="13" height="19" rx="1.5" stroke={color} strokeWidth={STROKE} />
    <path d="M12 7v10M9 8.5l6 7M15 8.5l-6 7" stroke={color} strokeWidth={STROKE * 0.9} strokeLinecap="round" />
  </svg>
);

export const ACIcon: FC<IconProps> = ({ size = 24, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <rect x="2.5" y="6" width="19" height="8" rx="1.5" stroke={color} strokeWidth={STROKE} />
    <line x1="2.5" y1="10.5" x2="21.5" y2="10.5" stroke={color} strokeWidth={STROKE * 0.9} />
    <path d="M7 17q2 2 4 0t4 0" stroke={color} strokeWidth={STROKE} strokeLinecap="round" fill="none" />
    <path d="M7 20q2 2 4 0t4 0" stroke={color} strokeWidth={STROKE} strokeLinecap="round" fill="none" opacity="0.55" />
  </svg>
);

export const BoilerIcon: FC<IconProps> = ({ size = 24, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <rect x="7" y="2.5" width="10" height="19" rx="1.5" stroke={color} strokeWidth={STROKE} />
    <circle cx="12" cy="8" r="1.4" stroke={color} strokeWidth={STROKE} />
    <path d="M12 14c1.2-1.4 1.2-2.8 0-4.2-1.2 1.4-1.2 2.8 0 4.2z" stroke={color} strokeWidth={STROKE * 0.9} strokeLinejoin="round" />
    <line x1="10" y1="20" x2="14" y2="20" stroke={color} strokeWidth={STROKE} strokeLinecap="round" />
  </svg>
);

export const DryerIcon: FC<IconProps> = ({ size = 24, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <rect x="4" y="3.5" width="16" height="17" rx="1.5" stroke={color} strokeWidth={STROKE} />
    <circle cx="12" cy="13" r="5" stroke={color} strokeWidth={STROKE} />
    <path d="M9.5 13q1-1.5 2.5 0t2.5 0" stroke={color} strokeWidth={STROKE} strokeLinecap="round" fill="none" />
    <circle cx="7.5" cy="6.5" r="0.7" fill={color} />
  </svg>
);

export const HeaterIcon: FC<IconProps> = ({ size = 24, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <rect x="3.5" y="5" width="17" height="14" rx="1.2" stroke={color} strokeWidth={STROKE} />
    <line x1="7" y1="5" x2="7" y2="19" stroke={color} strokeWidth={STROKE * 0.85} />
    <line x1="10" y1="5" x2="10" y2="19" stroke={color} strokeWidth={STROKE * 0.85} />
    <line x1="13" y1="5" x2="13" y2="19" stroke={color} strokeWidth={STROKE * 0.85} />
    <line x1="16" y1="5" x2="16" y2="19" stroke={color} strokeWidth={STROKE * 0.85} />
  </svg>
);

export const EVIcon: FC<IconProps> = ({ size = 24, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <rect x="4.5" y="3.5" width="11" height="17" rx="1.5" stroke={color} strokeWidth={STROKE} />
    <path d="M11 8l-2 4h2l-1 4 3-5h-2l1.5-3H11z" fill={color} />
    <path d="M15.5 9h3v6l-1 1" stroke={color} strokeWidth={STROKE} strokeLinecap="round" fill="none" />
  </svg>
);

export const BathIcon: FC<IconProps> = ({ size = 24, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path d="M3 11h18v3a4 4 0 01-4 4H7a4 4 0 01-4-4v-3z" stroke={color} strokeWidth={STROKE} strokeLinejoin="round" />
    <path d="M6 11V6.5a2 2 0 012-2h1" stroke={color} strokeWidth={STROKE} strokeLinecap="round" />
    <path d="M6 19v2M18 19v2" stroke={color} strokeWidth={STROKE} strokeLinecap="round" />
    <path d="M9 14.5q1 1 2 0t2 0t2 0" stroke={color} strokeWidth={STROKE * 0.8} strokeLinecap="round" fill="none" opacity="0.6" />
  </svg>
);

export const DishwasherIcon: FC<IconProps> = ({ size = 24, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <rect x="4" y="3.5" width="16" height="17" rx="1.5" stroke={color} strokeWidth={STROKE} />
    <line x1="4" y1="7" x2="20" y2="7" stroke={color} strokeWidth={STROKE} />
    <path d="M8 11v6M12 11v6M16 11v6" stroke={color} strokeWidth={STROKE * 0.9} strokeLinecap="round" opacity="0.7" />
    <circle cx="7.5" cy="5.2" r="0.5" fill={color} />
  </svg>
);

export const AutomationIcon: FC<IconProps> = ({ size = 24, color = 'currentColor' }) => (
  // Smart-home / automation control — IC chip with pins (REBT C11)
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <rect x="7" y="7" width="10" height="10" rx="1.2" stroke={color} strokeWidth={STROKE} />
    <rect x="10" y="10" width="4" height="4" rx="0.6" stroke={color} strokeWidth={STROKE} />
    <path
      d="M9.5 7V4.8M12 7V4.8M14.5 7V4.8M9.5 17v2.2M12 17v2.2M14.5 17v2.2M7 9.5H4.8M7 12H4.8M7 14.5H4.8M17 9.5h2.2M17 12h2.2M17 14.5h2.2"
      stroke={color}
      strokeWidth={STROKE * 0.8}
      strokeLinecap="round"
    />
  </svg>
);

export const CustomBlockIcon: FC<IconProps> = ({ size = 24, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <rect x="3.5" y="5" width="17" height="14" rx="1.8" stroke={color} strokeWidth={STROKE} />
    <line x1="8.5" y1="5" x2="8.5" y2="19" stroke={color} strokeWidth={STROKE * 0.85} opacity="0.6" />
    <line x1="15.5" y1="5" x2="15.5" y2="19" stroke={color} strokeWidth={STROKE * 0.85} opacity="0.6" />
    <circle cx="6" cy="12" r="1.1" fill={color} />
    <circle cx="12" cy="10" r="1" fill={color} />
    <circle cx="12" cy="14" r="1" fill={color} />
    <path d="M17.5 14.5l1.8-5" stroke={color} strokeWidth={STROKE} strokeLinecap="round" />
  </svg>
);

// ─── Topbar / chrome icons ───────────────────────────────────────────────────

export const UploadIcon: FC<IconProps> = ({ size = 16, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
    <path d="M8 2v8M5 5l3-3 3 3M2.5 12v1.5a1 1 0 001 1h9a1 1 0 001-1V12" stroke={color} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const UndoIcon: FC<IconProps> = ({ size = 16, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
    <path d="M3 7h7a3.5 3.5 0 010 7H6.5" stroke={color} strokeWidth="1.4" strokeLinecap="round" fill="none" />
    <path d="M3 7l3-3M3 7l3 3" stroke={color} strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);

export const TrashIcon: FC<IconProps> = ({ size = 16, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
    <path d="M3 4h10M6 4V2.5h4V4M4.5 4l.5 9.5a1 1 0 001 1h4a1 1 0 001-1L11.5 4" stroke={color} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const GridIcon: FC<IconProps> = ({ size = 16, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
    <rect x="2.5" y="2.5" width="11" height="11" rx="0.5" stroke={color} strokeWidth="1.4" />
    <line x1="6" y1="2.5" x2="6" y2="13.5" stroke={color} strokeWidth="1.4" />
    <line x1="10" y1="2.5" x2="10" y2="13.5" stroke={color} strokeWidth="1.4" />
    <line x1="2.5" y1="6" x2="13.5" y2="6" stroke={color} strokeWidth="1.4" />
    <line x1="2.5" y1="10" x2="13.5" y2="10" stroke={color} strokeWidth="1.4" />
  </svg>
);

export const PowerIcon: FC<IconProps> = ({ size = 16, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
    <path d="M5 4.5a4.5 4.5 0 106 0" stroke={color} strokeWidth="1.5" strokeLinecap="round" fill="none" />
    <line x1="8" y1="2" x2="8" y2="7" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

export const PlusIcon: FC<IconProps> = ({ size = 14, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 14 14" fill="none">
    <path d="M7 2v10M2 7h10" stroke={color} strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

export const PencilIcon: FC<IconProps> = ({ size = 12, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 12 12" fill="none">
    <path d="M2 10l1-3 5-5 2 2-5 5-3 1z" stroke={color} strokeWidth="1.2" strokeLinejoin="round" />
    <path d="M7 3l2 2" stroke={color} strokeWidth="1.2" />
  </svg>
);

export const CloseIcon: FC<IconProps> = ({ size = 12, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 12 12" fill="none">
    <path d="M2.5 2.5l7 7M9.5 2.5l-7 7" stroke={color} strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);

// ─── Element catalog ─────────────────────────────────────────────────────────
// Single source of truth: element type -> { Icon, label, sub, kw, group }.
// Replaces the old split between symbols.ts KW_MAP / GROUP_MAP / inline palette.

export type ElementGroup = 'light' | 'switch' | 'master' | 'socket' | 'appliance';

export interface CatalogEntry {
  Icon: FC<IconProps>;
  label: string;
  sub: string;
  kw: number;
  group: ElementGroup;
}

export const ELEMENT_CATALOG: Record<string, CatalogEntry> = {
  lighting_single:   { Icon: LampIcon,          label: 'Светильник',           sub: '60 Вт',      kw: 0.06, group: 'light' },
  exhaust_fan:       { Icon: ExhaustFanIcon,    label: 'Вытяжной вентилятор',  sub: '100 Вт',     kw: 0.10, group: 'light' },
  lighting_spot:     { Icon: SpotIcon,          label: 'Спот',                 sub: '50 Вт',      kw: 0.05, group: 'light' },
  switch:            { Icon: SwitchIcon,        label: 'Выключатель',          sub: '—',          kw: 0,    group: 'switch' },
  switch_double:     { Icon: DoubleSwitchIcon,  label: 'Выключатель (2)',      sub: '2 клавиши',  kw: 0,    group: 'switch' },
  master_switch:     { Icon: MasterSwitchIcon,  label: 'Мастер-выключатель',   sub: 'гасит свет', kw: 0,    group: 'master' },
  socket_single:     { Icon: Socket1Icon,       label: 'Розетка 220В',         sub: 'одиночная',  kw: 0,    group: 'socket' },
  socket_double:     { Icon: Socket2Icon,       label: 'Розетка двойная',      sub: 'сдвоенная',  kw: 0,    group: 'socket' },
  socket_triple:     { Icon: Socket3Icon,       label: 'Тройная розетка',      sub: '3 гнезда',   kw: 0,    group: 'socket' },
  socket_quadruple:  { Icon: Socket4Icon,       label: 'Четверная розетка',    sub: '4 гнезда',   kw: 0,    group: 'socket' },
  socket_3ph:        { Icon: Socket3phIcon,     label: 'Розетка 380В',         sub: 'трёхфазная', kw: 0,    group: 'socket' },
  bathroom_socket:   { Icon: BathSocketIcon,    label: 'Розетка в ванной',     sub: '1.0 кВт',    kw: 1.0,  group: 'socket' },
  kitchen_socket:    { Icon: KitchenSocketIcon, label: 'Кухонная розетка',     sub: '2.0 кВт',    kw: 2.0,  group: 'socket' },
  tv_socket:         { Icon: TvSocketIcon,      label: 'ТВ-розетка',           sub: 'TV',         kw: 0,    group: 'socket' },
  rj45_socket:       { Icon: Rj45SocketIcon,    label: 'Розетка RJ45',         sub: 'интернет',   kw: 0,    group: 'socket' },
  washing_machine:   { Icon: WasherIcon,        label: 'Стиральная машина',    sub: '2.2 кВт',    kw: 2.2,  group: 'appliance' },
  oven:              { Icon: OvenIcon,          label: 'Духовой шкаф',         sub: '3.5 кВт',    kw: 3.5,  group: 'appliance' },
  hob:               { Icon: HobIcon,           label: 'Варочная панель',      sub: '7.0 кВт',    kw: 7.0,  group: 'appliance' },
  microwave:         { Icon: MicrowaveIcon,     label: 'Микроволновка',        sub: '1.2 кВт',    kw: 1.2,  group: 'appliance' },
  extractor_hood:    { Icon: HoodIcon,          label: 'Вытяжка',              sub: '0.3 кВт',    kw: 0.3,  group: 'appliance' },
  refrigerator:      { Icon: FridgeIcon,        label: 'Холодильник',          sub: '0.3 кВт',    kw: 0.3,  group: 'appliance' },
  freezer:           { Icon: FreezerIcon,       label: 'Морозильник',          sub: '0.3 кВт',    kw: 0.3,  group: 'appliance' },
  air_conditioner:   { Icon: ACIcon,            label: 'Кондиционер',          sub: '2.0 кВт',    kw: 2.0,  group: 'appliance' },
  boiler:            { Icon: BoilerIcon,        label: 'Водонагреватель',      sub: '2.0 кВт',    kw: 2.0,  group: 'appliance' },
  dryer:             { Icon: DryerIcon,         label: 'Сушильная машина',     sub: '2.5 кВт',    kw: 2.5,  group: 'appliance' },
  electric_heater:   { Icon: HeaterIcon,        label: 'Электрообогреватель',  sub: '2.0 кВт',    kw: 2.0,  group: 'appliance' },
  ev_charger:        { Icon: EVIcon,            label: 'Зарядка EV',           sub: '3.6 кВт',    kw: 3.6,  group: 'appliance' },
  hydromassage_bath: { Icon: BathIcon,          label: 'Гидромассажная ванна', sub: '1.5 кВт',    kw: 1.5,  group: 'appliance' },
  dishwasher:        { Icon: DishwasherIcon,    label: 'Посудомойка',          sub: '1.8 кВт',    kw: 1.8,  group: 'appliance' },
  automation:        { Icon: AutomationIcon,    label: 'Автоматика',           sub: 'C11',        kw: 0.5,  group: 'appliance' },
  custom_block:      { Icon: CustomBlockIcon,   label: 'Блок',                 sub: 'розетки/выкл.', kw: 0,  group: 'appliance' },
};

// Palette grouping for the element palette UI.
export interface PaletteGroup {
  label: string;
  items: string[];
}

export const PALETTE_GROUPS: PaletteGroup[] = [
  { label: 'Освещение', items: ['lighting_single', 'exhaust_fan', 'lighting_spot', 'switch', 'switch_double', 'master_switch'] },
  { label: 'Блоки', items: ['custom_block'] },
  { label: 'Розетки', items: ['socket_single', 'socket_double', 'socket_triple', 'socket_quadruple', 'socket_3ph', 'bathroom_socket', 'kitchen_socket'] },
  {
    label: 'Бытовая техника',
    items: [
      'washing_machine', 'oven', 'hob', 'microwave', 'extractor_hood', 'refrigerator',
      'freezer', 'air_conditioner', 'boiler', 'dryer', 'electric_heater', 'ev_charger',
      'hydromassage_bath', 'dishwasher',
    ],
  },
];
